import { Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';

import { NgrxReportsConfiguration, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { PopupManager } from '@frontend/sports/popup/feature';
import { UrlService, UserService, WINDOW } from '@frontend/vanilla/core';
import { Action } from '@ngrx/store';

import { IBetslipState, defaultBetslipBaseState } from '../../../betslip/base/store/state';
import { defaultAffiliateState } from '../../../betslip/modules/affiliates/state/affiliate.state';
import { defaultBetslipPlaceBetState, defaultBetslipPlaceButtonState } from '../../../betslip/modules/betplacement/state';
import { defaultHiddenMarketState } from '../../../betslip/modules/hidden-market/hidden-market.state';
import { defaultInfoMessagesState } from '../../../betslip/modules/info-messages/state';
import { defaultLineSwitcherState } from '../../../betslip/modules/line-switcher/state';
import { defaultNotificationState } from '../../../betslip/modules/notifications/state';
import { defaultOddsPreferencePopupState } from '../../../betslip/modules/odds-preference-popup/odds-preference-state.model';
import { defaultOverAskState } from '../../../betslip/modules/over-ask/state';
import { defaultPickState } from '../../../betslip/modules/picks/state';
import { defaultQuickBetState } from '../../../betslip/modules/quick-bet/quick-bet.state';
import { defaultQuickDepositState } from '../../../betslip/modules/quick-deposit/quick-deposit.state';
import { defaultResultState } from '../../../betslip/modules/result/state';
import { defaultRewardTokensState } from '../../../betslip/modules/reward-tokens/state';
import { defaultSettingsState } from '../../../betslip/modules/settings/state';
import { defaultSourcePageState } from '../../../betslip/modules/source-page/source-page.state';
import { defaultTrackingState } from '../../../betslip/modules/tracking/state';
import { defaultBetslipTypeState } from '../../../betslip/modules/types/state';
import { defaultErrorState } from '../../../betslip/modules/validation/state';
import StateConverterService from '../../store-persist/state-converter.service';
import StoragePersister from '../../store-persist/storage-persister';
import { NgrxReportsService } from '../services/ngrx-reports.service';

const initialBetSlipState: IBetslipState = {
    base: defaultBetslipBaseState,
    picks: defaultPickState,
    types: defaultBetslipTypeState,
    errors: defaultErrorState,
    rewardTokens: defaultRewardTokensState,
    placeBet: defaultBetslipPlaceBetState,
    result: defaultResultState,
    settings: defaultSettingsState,
    placeButton: defaultBetslipPlaceButtonState,
    overAskState: defaultOverAskState,
    lineSwitcher: defaultLineSwitcherState,
    notifications: defaultNotificationState,
    affiliateState: defaultAffiliateState,
    quickDeposit: defaultQuickDepositState,
    quickBet: defaultQuickBetState,
    oddsPreferencePopupState: defaultOddsPreferencePopupState,
    hiddenMarket: defaultHiddenMarketState,
    sourcePageState: defaultSourcePageState,
    infoMessages: defaultInfoMessagesState,
    tracking: defaultTrackingState,
};

@Component({
    selector: 'ms-ngrx-reports',
    templateUrl: './ngrx-reports.component.html',
})
export class NgrxReportsComponent implements OnInit {
    protected logger: SportsRemoteLogger;
    submitted = false;
    submittedText: string = this.sitecore.ngrxReports.messages.SuccessfulSubmittedText;
    resetButtonText: string = this.sitecore.ngrxReports.messages.ReportsResetButtonText;
    submitButtonText: string = this.sitecore.ngrxReports.messages.ReportsSubmitButtonText;

    constructor(
        private ngrxReportsService: NgrxReportsService<Action>,
        private storePersistor: StoragePersister,
        private stateConverter: StateConverterService,
        loggerFactory: LoggerFactory,
        private sitecore: Sitecore,
        private userService: UserService,
        private popupManager: PopupManager,
        private ngrxReportsConfig: NgrxReportsConfiguration,
        private location: Location,
        private urlService: UrlService,
    ) {
        this.logger = loggerFactory.getLogger('NgrxReportsComponent');
    }

    ngOnInit(): void {
        if (!this.ngrxReportsConfig.isReportsEnabled) {
            this.popupManager.close();
        }
    }

    reset(): void {
        const convertedState = this.stateConverter.convert(initialBetSlipState);
        this.storePersistor.save(convertedState);
        const current = this.urlService.current();
        this.location.replaceState(current.path(), '');
        this.reloadPage();
    }

    submit(): void {
        this.submitted = true;
        const userName = this.userService.username;
        if (userName) this.logger.info(`UserName: ${userName}, Actions : ${JSON.stringify(this.ngrxReportsService.getActions())}`);
        else this.logger.info(`User Actions : ${JSON.stringify(this.ngrxReportsService.getActions())}`);
        setTimeout(() => {
            this.submitted = false;
        }, 3000);
    }

    reloadPage(): void {
        this._window.location.reload();
    }

    private readonly _window = inject(WINDOW);
}
